<template>
    <info-modal
        data-testid="addon-config-modal"
        :aria-label="'Configure Addon ' + addon.name"
        :data-qty-type="addon.qty_type"
    >
        <div class="flex flex-col gap-3">
            <img
                v-if="addon.thumbnail_url"
                :src="addon.thumbnail_url"
                class="w-32 rounded-md object-cover"
                :alt="addon.name"
                onerror="this.style.display='none'"
            />
            <h4 class="text-3xl font-semibold">{{ addon.name }}</h4>
            <product-price
                :price="addon.price"
                show-shipping-if-not-free
                class="text-base font-normal text-gray-700"
            ></product-price>
            <p class="my-2 text-gray-700">
                {{ addon.short_description }}
            </p>

            <div
                v-if="canSelectQty && addon.min_qty !== addon.max_qty"
                class="flex flex-col gap-2"
            >
                <label for="addon-qty" class="text-sm font-semibold">
                    Choose your quantity
                </label>
                <input
                    v-model="qty"
                    type="number"
                    :min="addon.min_qty ?? 1"
                    :max="addon.max_qty ?? null"
                    step="1"
                    id="addon-qty"
                    class="w-40 rounded border-gray-400 text-center"
                    :class="{ '!border-red-500': !isValid }"
                    data-testid="addon-qty"
                />
                <span
                    class="-mt-1 ml-1 text-xs italic text-red-600"
                    :class="{ invisible: isValid }"
                >
                    Min: {{ addon.min_qty ?? 1 }}
                    {{
                        addon.max_qty !== null ? '| Max: ' + addon.max_qty : ''
                    }}
                </span>
            </div>

            <div class="flex items-center space-x-4">
                <button
                    v-if="!isUpdate || (isUpdate && canSelectQty)"
                    type="button"
                    @click.prevent="selectAddon(addon)"
                    class="btn-black btn-short flex w-fit gap-1 text-sm font-normal disabled:cursor-not-allowed disabled:bg-gray-300"
                    data-testid="add-to-cart-btn"
                    :disabled="!isValid"
                >
                    {{ isUpdate ? 'Update' : 'Add to' }} booking
                    <plus-icon class="w-3"></plus-icon>
                </button>
                <a
                    v-if="isUpdate"
                    href="#"
                    @click.prevent="removeAddon(addon)"
                    data-testid="remove-from-cart-btn"
                    class="text-xs font-medium underline underline-offset-2"
                    >Remove</a
                >
            </div>
        </div>
    </info-modal>
</template>

<script setup>
import { useListingCart } from '@/composables/useListingCart';
import { PlusIcon } from '@heroicons/vue/outline';
import InfoModal from 'components/Global/InfoModal';
import ProductPrice from 'components/Global/ProductPrice.vue';
import { computed, ref } from 'vue';

const $emit = defineEmits(['addonSelected']);

const props = defineProps({
    // the listing for which the addon is configured
    product: {
        type: Object,
        required: true,
    },
    addon: {
        type: Object,
        required: true,
    },
});

const { addToCart, cartItemByUuid, removeFromCart } = useListingCart(
    props.product.uuid
);

const isUpdate = Boolean(cartItemByUuid(props.addon.uuid));
const canSelectQty = props.addon.qty_type === 'variable';
const qty = ref(
    canSelectQty
        ? (cartItemByUuid(props.addon.uuid)?.qty ?? props.addon.min_qty ?? 1)
        : null
);
const isValid = computed(() => {
    if (canSelectQty) {
        return (
            qty.value >= props.addon.min_qty &&
            (props.addon.max_qty === null
                ? true
                : qty.value <= props.addon.max_qty)
        );
    }
    return true;
});

const selectAddon = (addon) => {
    addToCart(addon, qty.value);
    $emit('addonSelected', addon);
};
const removeAddon = (addon) => {
    removeFromCart(addon.uuid);
    $emit('addonSelected', addon);
};
</script>

<style scoped></style>
